import { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "@core/features/form";
import {
  useFetchOrganization,
  useUpdateOrganization,
} from "@core/features/organization";
import { useCompleteOrgAction, ACTION_TYPE_SLUG } from "@core/features/actions";
import { Loader } from "@themes/pollinator/ui/Loader";
import { OrganizationProfileForm } from "./OrganizationProfileForm";
import { organizationProfileFormSchema } from "./organizationProfileForm.schema";

export type Props = {
  /** Optional organization ID override (defaults to URL param) */
  organizationId?: number;
  /** Optional callback when profile is saved successfully */
  onSaveSuccess?: () => void;
};

/**
 * Container component for OrganizationProfileForm.
 * Handles data fetching, mutations, and state management.
 *
 * Note: Authorization is handled at the page level (OrganizationAdminPage).
 * This container trusts that it's only rendered for authorized users.
 */
export function OrganizationProfileFormContainer({
  organizationId: organizationIdProp,
  onSaveSuccess,
}: Props) {
  const params = useParams<{ organizationId: string }>();
  const organizationId = organizationIdProp ?? Number(params.organizationId);

  const { data: organization, isLoading } = useFetchOrganization({
    organizationId,
    enabled: !!organizationId,
  });

  const { complete: completeOrgDisplayName } = useCompleteOrgAction({
    actionTypeSlug: ACTION_TYPE_SLUG.ORG_DISPLAY_NAME,
    organizationId,
  });

  const { complete: completeOrgUpdateSocials } = useCompleteOrgAction({
    actionTypeSlug: ACTION_TYPE_SLUG.ORG_UPDATE_SOCIALS,
    organizationId,
  });

  const mutation = useUpdateOrganization({
    organizationId,
    onSuccess: () => {
      completeOrgDisplayName();
      completeOrgUpdateSocials();
      onSaveSuccess?.();
    },
  });

  // Memoize defaultValues to ensure stable reference when organization data changes
  const defaultValues = useMemo(
    () => ({
      name: organization?.name ?? "",
      instagram_url: organization?.instagram_url ?? "",
      facebook_url: organization?.facebook_url ?? "",
      tiktok_url: organization?.tiktok_url ?? "",
      bluesky_url: organization?.bluesky_url ?? "",
      linkedin_url: organization?.linkedin_url ?? "",
    }),
    [organization]
  );

  const { form, handleSubmit } = useForm({
    schema: organizationProfileFormSchema,
    defaultValues,
    onSubmit: mutation.mutateAsync,
  });

  // Track if initial data has been loaded to prevent resetting after user edits
  const hasInitialDataLoaded = useRef(false);

  // Explicitly reset form when organization data first loads
  useEffect(() => {
    if (organization && !hasInitialDataLoaded.current) {
      hasInitialDataLoaded.current = true;
      form.reset({
        name: organization.name ?? "",
        instagram_url: organization.instagram_url ?? "",
        facebook_url: organization.facebook_url ?? "",
        tiktok_url: organization.tiktok_url ?? "",
        bluesky_url: organization.bluesky_url ?? "",
        linkedin_url: organization.linkedin_url ?? "",
      });
    }
  }, [organization, form]);

  const handleCancel = () => {
    form.reset();
  };

  const { isSubmitting, isValid, isDirty } = form.formState;

  if (isLoading || !organization) {
    return <Loader isFullPage />;
  }

  return (
    <OrganizationProfileForm
      form={form}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isDirty={isDirty}
      isValid={isValid}
      isSubmitting={isSubmitting}
    />
  );
}
